import { useState } from 'react';
import { useMutation } from 'react-query';

import useQueryParams from '~/hooks/useQueryParams';
import useToast from '~/hooks/useToast';

import { LoginErrorCode } from './types';

export function useStartMagicLinkFlowQuery() {
  return useMutation(async (email: string) => {
    const response = await fetch(`/auth/stytch/magic_link`, {
      method: 'POST',
      body: JSON.stringify({ email_address: email }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.ok;
  });
}

function identify(email: string) {
  if (!window.analytics) {
    return;
  }

  try {
    window.analytics.identify({ email });
  } catch (e) {
    // Ignore
  }
}

export default function useStartMagicLinkFlow() {
  const [email, setEmail] = useState('');
  const {
    mutateAsync,
    isLoading: isCreatingMagicLink,
  } = useStartMagicLinkFlowQuery();
  const queryParams = useQueryParams();
  const initialError = queryParams.get('error') as LoginErrorCode | null;

  const [error, setError] = useState(initialError);

  const { addSuccessToast } = useToast();

  const createMagicLink = async function() {
    if (!emailIsValid(email)) {
      return;
    }

    identify(email);

    if (await mutateAsync(email)) {
      addSuccessToast(
        'A secure sign in link has been sent.',
        'Please check your email.',
      );

      setError(null);

      setEmail('');
    }
  };

  function formHandler(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    createMagicLink();
  }

  return {
    email,
    setEmail,
    createMagicLink,
    formHandler,
    isCreatingMagicLink,
    error,
  };
}

export function emailIsValid(email: string | undefined): boolean {
  return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function buildErrorMessage(errorCode: LoginErrorCode): string {
  switch (errorCode) {
    case 'magic_link_invalid':
      return 'Your email link is invalid or has expired. Please try again.';
    case 'intermediate_session_not_found':
      return 'Your login session expired. Please try again.';
    case 'unauthorized':
      return 'There was a problem signing you in. Please try again.';
    default:
      return 'There was a problem signing you in. Please try again.';
  }
}
